import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Phone, Mail, CheckCircle2 } from "lucide-react";
import Footer from "@/components/honex/Footer";

export default function Danke() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-zinc-900 flex flex-col">
      {/* Header */}
      <header className="bg-zinc-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/assets/honex-mark-white.svg"
              alt="Honex GmbH"
              className="h-10 md:h-12 w-auto"
            />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-300 hover:text-red-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Zur Startseite
          </Link>
        </div>
      </header>

      {/* Dark hero section */}
      <section className="bg-zinc-950 text-white pb-24 pt-16 relative overflow-hidden">
        {/* Subtle background grid */}
        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-12 h-px bg-red-600" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-red-500">
              Honex GmbH
            </span>
          </div>

          {/* Check icon */}
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-600/15 border border-red-600/30 mb-8">
            <CheckCircle2 className="w-8 h-8 text-red-500" strokeWidth={1.5} />
          </div>

          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95] max-w-3xl">
            Vielen Dank für{" "}
            <span className="text-red-500">Ihre Anfrage!</span>
          </h1>
          <p className="mt-6 text-zinc-300 font-light max-w-2xl text-base sm:text-lg">
            Wir haben Ihre Nachricht erhalten und melden uns innerhalb der nächsten{" "}
            <strong className="text-white font-semibold">24 Stunden</strong> persönlich bei Ihnen.
          </p>
        </div>
      </section>

      {/* Content area */}
      <main className="flex-1">
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            {/* Main message */}
            <div className="md:col-span-7">
              <div className="text-[11px] font-bold uppercase tracking-widest text-red-600 mb-4">
                Wie geht es weiter?
              </div>
              <ul className="space-y-6">
                {[
                  {
                    step: "01",
                    title: "Prüfung Ihrer Anfrage",
                    text: "Unser Team sichtet Ihre Anfrage und bereitet alle relevanten Unterlagen vor.",
                  },
                  {
                    step: "02",
                    title: "Persönliche Kontaktaufnahme",
                    text: "Ein Ansprechpartner meldet sich telefonisch oder per E-Mail bei Ihnen, um Ihr Vorhaben zu besprechen.",
                  },
                  {
                    step: "03",
                    title: "Individuelles Angebot",
                    text: "Sie erhalten ein auf Ihr Projekt zugeschnittenes, transparentes Angebot ohne versteckte Kosten.",
                  },
                ].map(({ step, title, text }) => (
                  <li key={step} className="flex gap-5">
                    <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-zinc-200 bg-zinc-50 text-xs font-black text-zinc-400 tracking-widest">
                      {step}
                    </span>
                    <div>
                      <div className="font-bold text-zinc-900 tracking-tight">{title}</div>
                      <p className="mt-1 text-sm text-zinc-500 font-light leading-relaxed">{text}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-12">
                <Link
                  to="/"
                  className="inline-flex items-center gap-3 bg-red-600 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-red-700 transition-colors"
                >
                  Zurück zur Startseite
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Contact sidebar */}
            <div className="md:col-span-5">
              <div className="bg-zinc-950 text-white p-8">
                <div className="text-[11px] font-bold uppercase tracking-widest text-red-500 mb-6">
                  Direkter Kontakt
                </div>
                <p className="text-zinc-300 text-sm font-light mb-8 leading-relaxed">
                  Dringende Angelegenheit? Erreichen Sie uns auch direkt — wir sind für Sie da.
                </p>
                <div className="space-y-5">
                  <a
                    href="tel:+4920143355633"
                    className="flex items-center gap-4 group"
                  >
                    <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-zinc-800 group-hover:border-red-600 transition-colors">
                      <Phone className="w-4 h-4 text-red-500" />
                    </span>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-0.5">
                        Telefon
                      </div>
                      <span className="text-white text-sm font-semibold group-hover:text-red-400 transition-colors">
                        0201 43355633
                      </span>
                    </div>
                  </a>
                  <a
                    href="mailto:info@honexbau.de"
                    className="flex items-center gap-4 group"
                  >
                    <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-zinc-800 group-hover:border-red-600 transition-colors">
                      <Mail className="w-4 h-4 text-red-500" />
                    </span>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-0.5">
                        E-Mail
                      </div>
                      <span className="text-white text-sm font-semibold group-hover:text-red-400 transition-colors">
                        info@honexbau.de
                      </span>
                    </div>
                  </a>
                </div>

                <div className="mt-8 pt-8 border-t border-zinc-800">
                  <div className="text-xs text-zinc-500 font-light leading-relaxed">
                    Honex GmbH · Max-Keith-Str. 66 · 45136 Essen
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
